import React, {useCallback} from 'react';
import assert from 'assert-ts';
import type {PartValueRenderer} from 'schema/types';
import type {WorkItemLink} from 'services/workItems/types';
import {useLocalization} from 'localization';
import {isMetadataId} from 'services/utils';
import {useSetWorkItems} from 'services/workItems';
import {createMetadataLink} from 'services/workItems/functions';
import {CopyToClipboard, Link} from 'components';
import {useAdvancedSearchContext} from '../../../context/AdvancedSearchContext';

/**
 * Can only be used for ean/isbn and workId. Not manifestationId because we cannot
 * know wheather the uuid is workId or manifestationId.
 */
const LIMIT = 2000; // Limit for how many metadata links we fetch. It's a hard limit from backend aswell.

export const MetadataLink: PartValueRenderer = ({value, part}) => {
  const {t} = useLocalization();
  const setWorkItems = useSetWorkItems();
  const {getMetadataIdsPage} = useAdvancedSearchContext();

  const metadataId = isMetadataId(value) ? value : undefined;
  const originalValue =
    part.name === 'workId'
      ? metadataId?.workId
      : part.name === 'ean'
        ? metadataId?.ean
        : undefined;

  const onClick = useCallback(() => {
    setWorkItems([]);

    // Initiates promise, which will update redux with identifiers,
    // after the backend-call is completed.
    getMetadataIdsPage(0, LIMIT).then(allMetadataIds => {
      const filtered = allMetadataIds.filter(a => a.workId || a.ean);
      const currentIndex = filtered.findIndex(
        a => a.workId === metadataId?.workId || a.ean === metadataId?.ean,
      );

      // Remove all items before the one we clicked.
      filtered.splice(0, currentIndex + 1);
      const mapped = filtered.map(createMetadataLink) as string[];

      const workItems: WorkItemLink[] = mapped.map((link, index) => ({
        link,
        tooltip: `${t('page.search.next.tooltip')} (${index + 1} / ${mapped.length < LIMIT - 1 ? mapped.length : `${LIMIT}+`})`,
      }));

      setWorkItems(workItems);
    });
  }, [
    getMetadataIdsPage,
    metadataId?.ean,
    metadataId?.workId,
    setWorkItems,
    t,
  ]);

  if (!metadataId || !originalValue) {
    assert.soft(metadataId, 'MetadataLink: Expected metadataId', {
      value,
      part,
      metadataId,
      originalValue,
    });
    assert.soft(
      originalValue,
      'MetadataLink: Original value for MetadataLink is missing',
      {value, part, metadataId, originalValue},
    );

    return null;
  }

  return (
    <>
      <Link
        title={originalValue}
        to={createMetadataLink(metadataId) ?? ''}
        onClick={onClick}
      />

      <CopyToClipboard text={originalValue} size={'small'} />
    </>
  );
};
